import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bell } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { cn } from '../../lib/utils';
import { NotificationRow } from './NotificationRow';
import { useNotificationCenter, type NotifItem } from './notificationCenterStore';

/**
 * Header bell + dropdown — the quick-triage view of the admin activity inbox
 * (DESIGN: "A · Bell dropdown"). A 384px popover anchored under the bell with
 * Mark-all-read, All/Unread tabs, a scrollable list, and a "View all" footer
 * that deep-links to the full `/activity` screen. Shares state with that screen
 * via `useNotificationCenter`.
 */
export function NotificationBell() {
  const navigate = useNavigate();
  const { items, unread, markAll, read } = useNotificationCenter();
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState<'all' | 'unread'>('all');

  const shown = tab === 'unread' ? items.filter((n) => n.unread) : items;

  const activate = (item: NotifItem) => {
    read(item.id);
    setOpen(false);
    navigate(item.to);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          aria-label="Notifications"
          className={cn(
            'relative flex size-9 shrink-0 items-center justify-center rounded-pill border border-hairline transition-colors duration-150',
            open ? 'bg-indigo-tint text-indigo' : 'text-ink-2 hover:bg-surface-2 hover:text-ink',
          )}
        >
          <Bell className="size-[18px]" />
          {unread > 0 && (
            <span className="absolute -right-1 -top-1 flex h-[17px] min-w-[17px] items-center justify-center rounded-pill border-2 border-background bg-orange px-1 text-[10.5px] font-bold leading-none text-white">
              {unread}
            </span>
          )}
        </button>
      </PopoverTrigger>

      <PopoverContent
        align="end"
        sideOffset={10}
        className="flex max-h-[540px] w-96 flex-col overflow-hidden rounded-[16px] p-0"
      >
        {/* header */}
        <div className="flex items-center gap-2.5 border-b border-hairline px-4 pb-3 pt-3.5">
          <span className="text-[15.5px] font-bold tracking-tight text-ink">Notifications</span>
          {unread > 0 && (
            <span className="inline-flex h-5 min-w-5 items-center justify-center rounded-pill bg-indigo px-1.5 text-[11.5px] font-bold text-white">
              {unread}
            </span>
          )}
          <span className="flex-1" />
          <button
            type="button"
            onClick={markAll}
            className="text-[12.5px] font-semibold text-indigo hover:text-indigo-press"
          >
            Mark all read
          </button>
        </div>

        {/* tabs */}
        <div className="mx-3 mb-1 mt-2.5 flex gap-1 rounded-pill bg-surface-2 p-1">
          {([
            ['all', 'All'],
            ['unread', `Unread${unread ? ` · ${unread}` : ''}`],
          ] as const).map(([id, label]) => {
            const active = tab === id;
            return (
              <button
                key={id}
                type="button"
                onClick={() => setTab(id)}
                className={cn(
                  'h-8 flex-1 rounded-pill text-[13px] transition-colors duration-150',
                  active
                    ? 'bg-background font-semibold text-ink shadow-latio-sm'
                    : 'font-medium text-ink-2 hover:text-ink',
                )}
              >
                {label}
              </button>
            );
          })}
        </div>

        {/* list */}
        <div className="flex-1 overflow-auto px-2 pb-2 pt-1">
          {shown.length === 0 ? (
            <div className="py-10 text-center text-[13.5px] text-ink-3">You’re all caught up.</div>
          ) : (
            shown.map((item) => (
              <NotificationRow key={item.id} item={item} onActivate={() => activate(item)} />
            ))
          )}
        </div>

        {/* footer */}
        <button
          type="button"
          onClick={() => {
            setOpen(false);
            navigate('/activity');
          }}
          className="h-[46px] shrink-0 border-t border-hairline text-[13.5px] font-semibold text-indigo hover:bg-surface-2"
        >
          View all notifications
        </button>
      </PopoverContent>
    </Popover>
  );
}
